import React, { useState, useEffect } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveTiming = () => {
  const [records, setRecords] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/live-timing/records?limit=50`);
      setEnabled(response.data.enabled);
      if (response.data.enabled) {
        setRecords(response.data.records);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch live timing data');
    } finally {
      setLoading(false);
    }
  };

  const groupByCarNumber = () => {
    const grouped = {};
    records.forEach(record => {
      if (!grouped[record.car_number]) {
        grouped[record.car_number] = {
          carNumber: record.car_number,
          lastUpdate: record.timestamp,
          records: []
        };
      }
      grouped[record.car_number].records.push(record);
    });
    return Object.values(grouped).sort((a, b) => 
      new Date(b.lastUpdate) - new Date(a.lastUpdate)
    );
  };

  const getLatestRecordType = (carRecords) => {
    if (carRecords.length === 0) return 'Unknown';
    return carRecords[0].timing_type.replace('_', ' ');
  };

  const carData = groupByCarNumber();

  if (!enabled) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="text-center">
          <WifiOff size={64} className="text-[#ef4444] mx-auto mb-4" />
          <h1 className="text-3xl font-black text-white mb-2">LIVE TIMING DISABLED</h1>
          <p className="text-zinc-400">Live timing is currently disabled by the administrator</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-black text-white tracking-wider">LIVE TIMING</h1>
            <p className="text-zinc-400 mono-text text-sm mt-2">
              Last Update: {lastUpdate.toLocaleTimeString()}
            </p>
          </div>
          <Button
            onClick={fetchLiveData}
            disabled={loading}
            className="bg-[#facc15] text-black hover:bg-[#eab308]"
            data-testid="refresh-live-button"
          >
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
            REFRESH
          </Button>
        </div>

        {/* Live Indicator */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4 mb-6 flex items-center gap-3">
          <div className="status-indicator status-online" />
          <span className="text-white font-bold uppercase-label">LIVE</span>
          <span className="text-zinc-400 text-sm">Auto-refreshing every 5 seconds</span>
        </div>

        {/* Timing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {carData.map(car => (
            <div
              key={car.carNumber}
              className="bg-[#18181b] border border-[#27272a] rounded-lg p-6 card-strip-green"
              data-testid={`live-car-${car.carNumber}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-3xl font-black text-[#facc15] mono-text">#{car.carNumber}</h2>
                <span className="text-xs text-zinc-500 uppercase-label">
                  {car.records.length} records
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs uppercase-label text-zinc-400 mb-1">Latest</p>
                  <p className="text-white font-bold">{getLatestRecordType(car.records)}</p>
                </div>

                <div>
                  <p className="text-xs uppercase-label text-zinc-400 mb-1">Time</p>
                  <p className="text-white mono-text text-sm">
                    {new Date(car.lastUpdate).toLocaleTimeString()}
                  </p>
                </div>

                <div className="pt-3 border-t border-[#27272a]">
                  <p className="text-xs uppercase-label text-zinc-400 mb-2">All Records</p>
                  <div className="space-y-1">
                    {car.records.slice(0, 5).map((record, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-zinc-400 uppercase-label">
                          {record.timing_type.replace('_', ' ')}
                        </span>
                        <span className="text-zinc-500 mono-text">
                          {new Date(record.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {carData.length === 0 && (
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-12 text-center">
            <p className="text-zinc-400 text-lg">No timing data available yet</p>
            <p className="text-zinc-500 text-sm mt-2">Records will appear here as they are recorded</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveTiming;